import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import type { ReactNode, RefObject } from 'react'
import * as z from 'zod'
import { Autocomplete } from '../../../../components'
import type { InputMaskedProps } from '../../../../components/InputMasked'
import { clsx } from 'clsx'
import type {
  CountryISO,
  CountryLang,
  CountryType,
} from '../../constants/countries'
import useCountries from '../SelectCountry/useCountries'
import type { FieldStringProps as StringFieldProps } from '../String'
import StringField from '../String'
import type { FieldCompositionProps as CompositionFieldProps } from '../Composition'
import CompositionField from '../Composition'
import { useFieldProps } from '../../hooks'
import type { FieldPropsWithExtraValue, Schema } from '../../types'
import { pickSpacingProps } from '../../../../components/flex/utils'
import SharedContext from '../../../../shared/Context'
import type { CountryFilterSet } from '../SelectCountry'
import { countryFilter, getCountryData } from '../SelectCountry'
import detectCountryCode from '../../../../shared/detectCountryCode'
import useTranslation from '../../hooks/useTranslation'
import type { DrawerListDataArrayItem } from '../../../../fragments/DrawerList'
import type { AutocompleteOnChangeParams } from '../../../../components/Autocomplete'
import withComponentMarkers from '../../../../shared/helpers/withComponentMarkers'

export type AdditionalArgs = {
  phoneNumber: string
  countryCode: string
  iso?: string
}

export type FieldPhoneNumberProps = Omit<
  FieldPropsWithExtraValue<string, AdditionalArgs, undefined | string>,
  'layout' | 'layoutOptions' | 'labelSize' | 'onBlurValidator'
> & {
  onBlurValidator?:
    | FieldPropsWithExtraValue<
        string,
        AdditionalArgs,
        undefined | string
      >['onBlurValidator']
    | false
  countryCodeFieldClassName?: string
  numberFieldClassName?: string
  countryCodePlaceholder?: string
  countryCodeLabel?: ReactNode | false
  numberLabel?: ReactNode | false
  numberMask?: InputMaskedProps['mask']
  pattern?: StringFieldProps['pattern']
  width?: 'large' | 'stretch'
  inputRef?: RefObject<HTMLInputElement>
  omitCountryCodeField?: boolean
  onCountryCodeChange?: (value: string | undefined) => void
  onNumberChange?: (value: string | undefined) => void

  /**
   * List only a certain set of countries: `Scandinavia`, `Nordic`, `Europe` or `Prioritized` (all countries [sorted by priority](/uilib/extensions/forms/feature-fields/SelectCountry/#filter-or-prioritize-country-listing)). Defaults to `Prioritized`.
   */
  countries?: CountryFilterSet

  /**
   * Use this property to filter out certain countries. The function receives the country object and should return a boolean. Returning `false` will omit the country.
   */
  filterCountries?: (country: CountryType) => boolean

  /**
   * For internal testing purposes
   */
  noAnimation?: boolean
} & Pick<StringFieldProps, 'size'>

// Important for the default value to be defined here, and not after the useFieldProps call, to avoid the UI jumping
// back to +47 once the user empty the field so handleChange send out undefined.
const defaultCountryCode = '+47'
const defaultMask = [
  /\d/,
  /\d/,
  ' ',
  /\d/,
  /\d/,
  ' ',
  /\d/,
  /\d/,
  ' ',
  /\d/,
  /\d/,
]

type EventValues = {
  countryCode?: string
  phoneNumber?: string
}

function PhoneNumber(props: FieldPhoneNumberProps = {}) {
  const sharedContext = useContext(SharedContext)
  const {
    numberLabel: defaultLabel,
    countryCodeLabel: defaultCountryCodeLabel,
    errorRequired,
    errorLengthNorwegianPhoneNumbers,
  } = useTranslation().PhoneNumber
  const lang = sharedContext.locale?.split('-')[0] as CountryLang

  const countryCodeRef = useRef<FieldPhoneNumberProps['value']>(
    props.emptyValue
  )
  const prevCountryCodeRef = useRef(countryCodeRef.current)
  const numberRef = useRef<FieldPhoneNumberProps['value']>(
    props.emptyValue
  )
  const dataRef = useRef<Array<DrawerListDataArrayItem> | null>(null)
  const langRef = useRef<string>(lang)
  const wasFilled = useRef<boolean>(false)
  const currentCountryRef = useRef<CountryType>(undefined)

  const errorMessages = useMemo(
    () => ({
      'Field.errorRequired': errorRequired,
      'Field.errorPattern': errorRequired,
      ...props.errorMessages,
    }),
    [errorRequired, props.errorMessages]
  )

  const validateRequired = useCallback(
    (value: string, { required, isChanged, error }) => {
      if (required) {
        const [countryCode, phoneNumber] = splitValue(value)

        if (countryCode !== prevCountryCodeRef.current) {
          if (countryCode) {
            prevCountryCodeRef.current = countryCode
          }
          return undefined
        }

        if (isChanged && !phoneNumber) {
          return error
        }
      }

      return undefined
    },
    []
  )

  const fromExternal = useCallback(
    (external: string) => {
      if (typeof external === 'string') {
        const [countryCode, phoneNumber] = splitValue(external)
        if (!countryCode && !phoneNumber && !props.omitCountryCodeField) {
          return countryCodeRef.current
        }

        // Normalize to E.164 format so the stored value is always spaceless
        if (countryCode && phoneNumber) {
          return toE164([countryCode, phoneNumber])
        }
      }
      return external
    },
    [props.omitCountryCodeField]
  )

  const toEvent = useCallback(
    (value: string) => {
      const [, phoneNumber] = splitValue(value)
      if (!phoneNumber) {
        return props.emptyValue
      }
      return value
    },
    [props.emptyValue]
  )

  const customTransformIn = props.transformIn
  const transformIn = useCallback(
    (value: string) => {
      if (customTransformIn) {
        const external = customTransformIn(value)

        if (typeof external === 'string') {
          return external
        }

        if (external?.phoneNumber) {
          return toE164([external.countryCode, external.phoneNumber])
        }
      }

      return value
    },
    [customTransformIn]
  )

  const provideAdditionalArgs = useCallback(
    (value: string) => {
      const [countryCode, phoneNumber] = splitValue(value)

      return {
        ...(!props.omitCountryCodeField
          ? {
              countryCode:
                countryCode || countryCodeRef.current || undefined,
            }
          : {}),
        phoneNumber: phoneNumber || numberRef.current || undefined,
        iso: currentCountryRef.current?.iso,
      } satisfies AdditionalArgs | Omit<AdditionalArgs, 'countryCode'>
    },
    [props.omitCountryCodeField]
  )

  const schema = useMemo<Schema<string> | undefined>(() => {
    if (props.schema) {
      return props.schema
    }

    if (!props.pattern) return undefined
    // Use Zod internally when only pattern is provided
    return (p: FieldPhoneNumberProps) => {
      let s = z.string()
      if (p?.pattern) {
        try {
          s = s.regex(new RegExp(p.pattern, 'u'), 'Field.errorPattern')
        } catch (_e) {
          // Ignore invalid regex patterns
        }
      }
      return s
    }
  }, [props.schema, props.pattern])

  // Validates that Norwegian phone numbers (+47) don't exceed 8 digits.
  // This maintains existing behavior — before allowOverflow was added,
  // the input mask physically prevented typing more than 8 digits for Norwegian numbers.
  const norwegianPhoneNumberLengthValidator = useCallback(
    (value: string) => {
      if (props.numberMask) {
        return undefined
      }

      const [countryCode, phoneNumber] = splitValue(value)
      if (countryCode === defaultCountryCode && phoneNumber?.length > 8) {
        return Error(errorLengthNorwegianPhoneNumbers)
      }

      return undefined
    },
    [props.numberMask, errorLengthNorwegianPhoneNumbers]
  )

  const onBlurValidatorToUse =
    props.onBlurValidator === false || props.pattern || props.schema
      ? undefined
      : (props.onBlurValidator ?? norwegianPhoneNumberLengthValidator)
  const phoneNumberDefaultProps: Partial<FieldPhoneNumberProps> = {
    ...(schema ? { schema } : {}),
    errorMessages,
  }
  const ref = useRef<HTMLInputElement>(undefined)
  const preparedProps = {
    ...props,
    ...phoneNumberDefaultProps,
    onBlurValidator: onBlurValidatorToUse,
    validateRequired,
    fromExternal,
    toEvent,
    provideAdditionalArgs,
    transformIn,
    inputRef: props.inputRef ?? ref,
  } satisfies FieldPhoneNumberProps

  const {
    id,
    path,
    itemPath,
    value,
    className,
    inputRef,
    countryCodeFieldClassName,
    numberFieldClassName,
    countryCodePlaceholder,
    placeholder,
    countryCodeLabel,
    label,
    labelDescription,
    numberLabel,
    labelSrOnly,
    numberMask,
    countries: ccFilter = 'Prioritized',
    emptyValue,
    info,
    warning,
    size,
    error,
    hasError,
    disabled,
    width,
    help,
    required,
    validateInitially,
    validateContinuously,
    validateUnchanged,
    omitCountryCodeField,
    setHasFocus,
    handleChange,
    setDisplayValue,
    onCountryCodeChange,
    onNumberChange,
    filterCountries,
  } = useFieldProps(preparedProps, {
    executeOnChangeRegardlessOfUnchangedValue: true,
  })

  useEffect(() => {
    if (path || itemPath) {
      const number = inputRef.current?.value
      setDisplayValue(
        number?.length > 0
          ? `${countryCodeRef.current} ${number}`
          : undefined
      )
    }
  }, [inputRef, itemPath, path, setDisplayValue, value])

  const filter = useCallback(
    (country: CountryType) => {
      return countryFilter(country, filterCountries, ccFilter)
    },
    [ccFilter, filterCountries]
  )

  const { countries } = useCountries()

  const updateCurrentDataSet = useCallback(() => {
    dataRef.current = getCountryData({
      countries,
      lang,
      filter:
        // Make sure the whole cc list is displayed when cc filter is set to specific region
        ccFilter === 'Prioritized' && !wasFilled.current
          ? (country) =>
              `${formatCountryCode(country.cdc)}` ===
              countryCodeRef.current
          : filter,
      sort: ccFilter as Extract<CountryFilterSet, 'Prioritized'>,
      makeObject,
    })
  }, [countries, lang, ccFilter, filter])

  const prepareEventValues = useCallback(
    ({
      countryCode = countryCodeRef.current || emptyValue,
      phoneNumber = numberRef.current || emptyValue,
    }: EventValues = {}) => {
      if (!currentCountryRef.current) {
        type Item = DrawerListDataArrayItem & { country: CountryType }

        const cdcVal = countryCode?.replace(/^\+/, '').replace(/-/g, '')
        const item = dataRef.current.find((item) => {
          const cdc = (item as Item)?.country?.cdc?.replace(/-/g, '')
          return cdc === cdcVal
        }) as Item

        currentCountryRef.current = item?.country
      }

      return {
        ...(!omitCountryCodeField ? { countryCode } : {}),
        phoneNumber,
        iso: currentCountryRef.current?.iso as CountryISO,
      }
    },
    [emptyValue, omitCountryCodeField]
  )

  const callOnChange = useCallback(
    (data: EventValues) => {
      const eventValues = prepareEventValues(data)

      handleChange(
        toEvent(
          toE164([eventValues.countryCode, eventValues.phoneNumber])
        ),
        eventValues
      )
    },
    [prepareEventValues, handleChange]
  )

  const callOnBlurOrFocus = useCallback(
    (hasFocus: boolean) => {
      const eventValues = prepareEventValues()
      setHasFocus(hasFocus, undefined, eventValues)
    },
    [prepareEventValues, setHasFocus]
  )

  /**
   * We do not process the whole country list at the first render.
   * Only when the Autocomplete opens (focus).
   * To achieve this, we use memo instead of effect to update refs in sync.
   *
   * We set or update the data list depending on if the country code changes or lang changes.
   * We then update countryCode and phoneNumber when value changes.
   */
  useMemo(() => {
    const [countryCode, phoneNumber] = splitValue(props.value || value)
    numberRef.current = phoneNumber

    if (lang !== langRef.current || !wasFilled.current) {
      if (!countryCodeRef.current || countryCode) {
        countryCodeRef.current = countryCode || defaultCountryCode
      }
      langRef.current = lang

      updateCurrentDataSet()
    }
  }, [value, props.value, lang, updateCurrentDataSet])

  const handleCountryCodeChange = useCallback(
    (event: AutocompleteOnChangeParams) => {
      const data = event.data
      const dataObj =
        data && typeof data === 'object' && 'selectedKey' in data
          ? (data as unknown as {
              selectedKey: string
              country: CountryType
            })
          : undefined
      const countryCode = (countryCodeRef.current =
        dataObj?.selectedKey?.trim() || emptyValue)
      currentCountryRef.current = dataObj?.country

      callOnChange({ countryCode })
      onCountryCodeChange?.(countryCode)
    },
    [emptyValue, onCountryCodeChange, callOnChange]
  )

  const handleNumberChange = useCallback(
    (value: string) => {
      const phoneNumber = (numberRef.current = value || emptyValue)

      callOnChange({ phoneNumber })
      onNumberChange?.(phoneNumber)
    },
    [emptyValue, callOnChange, onNumberChange]
  )

  const handleOnBlur = useCallback(() => {
    callOnBlurOrFocus(false)
  }, [callOnBlurOrFocus])

  const handleOnFocus = useCallback(() => {
    callOnBlurOrFocus(true)
  }, [callOnBlurOrFocus])

  const handleCountryCodeFocus = useCallback(
    ({ updateData }) => {
      if (!wasFilled.current) {
        wasFilled.current = true
        updateCurrentDataSet()
        updateData(dataRef.current)
      }
      handleOnFocus()
    },
    [handleOnFocus, updateCurrentDataSet]
  )

  const onTypeHandler = useCallback(
    ({ value, updateData, revalidateInputValue, event }) => {
      // Handle browser autofill/autocomplete
      if (typeof event?.nativeEvent?.data === 'undefined') {
        // Try to detect the country code from the autofilled value.
        // Browsers may autofill either space-separated ("+47 12345678")
        // or spaceless E.164 ("+4712345678") values.
        const detected = detectCountryCode(value)
        const cdcVal = detected
          ? detected.countryCode.replace(/^\+/, '').replace(/-/g, '')
          : value
        const country = countries.find(
          ({ cdc }) => cdc.replace(/-/g, '') === cdcVal?.replace(/-/g, '')
        )
        if (country?.cdc) {
          const countryCode = (countryCodeRef.current = formatCountryCode(
            country.cdc
          ))

          updateCurrentDataSet()
          updateData(dataRef.current)
          callOnChange({ countryCode })

          // To ensure correct input value,
          // regardless if there is changed data before or not.
          window.requestAnimationFrame(() => {
            revalidateInputValue()
          })
        }
      }
    },
    [callOnChange, countries, updateCurrentDataSet]
  )

  const isDefault = countryCodeRef.current?.includes(defaultCountryCode)

  const compositionFieldProps: CompositionFieldProps = {
    id,
    className: clsx('dnb-forms-field-phone-number', className),
    width: 'stretch',
    label,
    labelDescription,
    labelSrOnly,
    help: undefined,
    ...pickSpacingProps(props),
  }

  return (
    <CompositionField {...compositionFieldProps}>
      {!omitCountryCodeField && (
        <Autocomplete
          className={clsx(
            'dnb-forms-field-phone-number__country-code',
            countryCodeFieldClassName
          )}
          mode="async"
          placeholder={countryCodePlaceholder}
          labelDirection="vertical"
          label={
            countryCodeLabel === false
              ? defaultCountryCodeLabel
              : (countryCodeLabel ?? defaultCountryCodeLabel)
          }
          labelSrOnly={countryCodeLabel === false ? true : undefined}
          data={dataRef.current}
          value={countryCodeRef.current}
          status={hasError ? 'error' : undefined}
          disabled={disabled}
          onFocus={handleCountryCodeFocus}
          onBlur={handleOnBlur}
          onChange={handleCountryCodeChange}
          onType={onTypeHandler}
          independentWidth
          searchNumbers
          keepSelection
          selectAll
          autoComplete="tel-country-code"
          noAnimation={props.noAnimation}
          size={size}
        />
      )}
      <StringField
        className={clsx(
          'dnb-forms-field-phone-number__number',
          numberFieldClassName
        )}
        type="tel"
        autoComplete="tel-national"
        emptyValue={emptyValue}
        layout="vertical"
        label={
          numberLabel === false
            ? defaultLabel
            : (numberLabel ?? defaultLabel)
        }
        labelSrOnly={numberLabel === false ? true : undefined}
        placeholder={placeholder}
        mask={
          // E.164 allows up to 15 digits total (country code + subscriber number).
          // The country code (1–3 digits) is in a separate field, so the subscriber
          // number can be up to 14 digits. We use 15 as a safe upper bound.
          numberMask ?? (isDefault ? defaultMask : Array(15).fill(/\d/))
        }
        allowOverflow
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onChange={handleNumberChange}
        value={numberRef.current}
        ref={inputRef}
        info={info}
        warning={warning}
        error={error}
        disabled={disabled}
        width={
          width === 'stretch'
            ? 'stretch'
            : props.omitCountryCodeField && width === 'large'
              ? 'large'
              : 'medium'
        }
        help={{ ...help, breakout: false, outset: false }}
        required={required}
        errorMessages={errorMessages}
        validateInitially={validateInitially}
        validateContinuously={validateContinuously}
        validateUnchanged={validateUnchanged}
        inputMode="tel"
        size={size}
      />
    </CompositionField>
  )
}

function makeObject(country: CountryType, lang: string) {
  const name = country.i18n[lang] ?? country.i18n.en
  const code = formatCountryCode(country.cdc)
  return {
    selectedKey: code,
    selectedValue: `${country.iso} (${code})`,
    searchContent: [code, name],
    content: [name, code],
    country,
  }
}

function formatCountryCode(value: string) {
  return `+${value}`
}

function splitValue(value: string) {
  if (typeof value !== 'string') {
    return [undefined, '']
  }

  // Values with spaces are not valid E.164 — reject them
  if (value.includes(' ')) {
    return [undefined, '']
  }

  // Auto-detect country code from spaceless strings like "+4712345678" or "004712345678"
  // detectCountryCode handles 00→+ normalization internally and only succeeds
  // when there are enough digits for both a country code and subscriber number,
  // so short values like "007" are left unchanged.
  const detected = detectCountryCode(value)
  if (detected) {
    return [detected.countryCode, detected.phoneNumber]
  }

  // No space found — treat the whole value as the country code (or plain text)
  if (value.startsWith('+')) {
    return [value, '']
  }

  return [undefined, value]
}

function toE164(array: Array<string>) {
  return array
    .filter(Boolean)
    .map((part) => part.replace(/-/g, ''))
    .join('')
}

withComponentMarkers(PhoneNumber, {
  _supportsSpacingProps: undefined,
})

export default PhoneNumber
